import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, MessageCircle } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import PersonMessageComposer from './PersonMessageComposer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function personName(employee, profileMode) {
  const name = [
    employee?.first_name || employee?.preferred_name,
    employee?.last_name,
  ].filter(Boolean).join(' ').trim();
  return name || (profileMode === 'applicant' ? 'Applicant' : 'Employee');
}

function staffCapabilities(payload) {
  const capabilities = payload?.capabilities || {};
  return {
    emailEnabled: capabilities?.email?.enabled !== false,
    emailReason: capabilities?.email?.reason || capabilities?.read_only_reason || '',
    readOnly: capabilities?.read_only === true,
  };
}

export default function ProfileMessageDialog({
  open,
  onOpenChange,
  employeeId,
  employee,
  profileMode = 'employee',
  nextActionTitle = '',
}) {
  const { token } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const applicant = profileMode === 'applicant';

  const load = useCallback(async ({ background = false } = {}) => {
    if (!open || !employeeId || !token) return;
    if (!background) setLoading(true);
    setError('');
    try {
      const endpoint = applicant
        ? `${API}/employees/${employeeId}/communications`
        : `${API}/employees/${employeeId}/staff-communications`;
      const response = await axios.get(endpoint, { headers });
      setPayload(response.data || {});
    } catch (requestError) {
      setPayload(null);
      const detail = requestError?.response?.data?.detail;
      setError(
        typeof detail === 'string' && detail.trim()
          ? detail
          : 'Messaging could not be loaded for this person.',
      );
    } finally {
      if (!background) setLoading(false);
    }
  }, [applicant, employeeId, headers, open, token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open || !employeeId || !token) return undefined;
    const intervalId = window.setInterval(() => {
      load({ background: true });
    }, 8000);
    return () => window.clearInterval(intervalId);
  }, [employeeId, load, open, token]);

  const capabilities = applicant
    ? { emailEnabled: true, emailReason: '', readOnly: false }
    : staffCapabilities(payload);

  const messages = Array.isArray(payload?.messages) ? payload.messages : [];
  const thread = payload?.thread || null;
  const name = personName(employee, profileMode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] overflow-x-hidden overflow-y-auto p-4 sm:w-[94vw] sm:max-w-5xl sm:p-6" data-testid="profile-message-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2 pr-8 text-left leading-6 sm:items-center">
            <MessageCircle className="h-5 w-5 text-primary" />
            Message {name}
          </DialogTitle>
          <DialogDescription className="text-left leading-5">
            {applicant
              ? 'Messages sent through CareOps stay in Recruitment history. A copied manual WhatsApp message is not recorded as sent.'
              : 'Employee messages stay in Staff Communications.'}
          </DialogDescription>
        </DialogHeader>

        {applicant && nextActionTitle ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950" data-testid="message-next-onboarding-action">
            <span className="font-semibold">Current onboarding action:</span> {nextActionTitle}
          </div>
        ) : null}

        {loading ? (
          <div className="flex min-h-[180px] items-center justify-center gap-2 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading messaging…
          </div>
        ) : error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            {error}
          </div>
        ) : (
          <PersonMessageComposer
            domain={applicant ? 'recruitment' : 'staff'}
            employeeId={employeeId}
            token={token}
            messages={messages}
            threadId={thread?.id || null}
            emailEnabled={capabilities.emailEnabled}
            emailReason={capabilities.emailReason}
            readOnly={capabilities.readOnly}
            personFirstName={employee?.first_name || employee?.preferred_name || ''}
            nextActionTitle={nextActionTitle}
            initialChannel={applicant ? 'whatsapp' : 'email'}
            onSent={() => load({ background: true })}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
